import React, { useState } from 'react'
import {
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Card,
    CardContent,
    CircularProgress,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/DeleteForever'
import { useTheme } from '@material-ui/core/styles'
import { Container } from '../../Container'
import {
    Topic,
    TopicInput,
    useTopics,
    useCreateTopic,
    useUpdateTopic,
    useDeleteTopic,
} from '../../../hooks/useTopics'

interface TopicFormState {
    name: string
    description: string
    keywords: string
}

const EMPTY_FORM: TopicFormState = { name: '', description: '', keywords: '' }

function formatTopicName(name: string): string {
    return name
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

export const TopicsTab = () => {
    const theme = useTheme()
    const { topics, isLoading } = useTopics()
    const createTopic = useCreateTopic()
    const updateTopic = useUpdateTopic()
    const deleteTopic = useDeleteTopic()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingTopic, setEditingTopic] = useState<Topic | null>(null)
    const [form, setForm] = useState<TopicFormState>({ ...EMPTY_FORM })

    const handleOpenAdd = () => {
        setEditingTopic(null)
        setForm({ ...EMPTY_FORM })
        setDialogOpen(true)
    }

    const handleOpenEdit = (topic: Topic) => {
        setEditingTopic(topic)
        setForm({
            name: topic.name,
            description: topic.description ?? '',
            keywords: topic.keywords ?? '',
        })
        setDialogOpen(true)
    }

    const handleClose = () => {
        setDialogOpen(false)
        setForm({ ...EMPTY_FORM })
        setEditingTopic(null)
    }

    const handleSave = async () => {
        if (!form.name.trim()) return

        const input: TopicInput = {
            name: form.name.trim(),
            description: form.description.trim() || null,
            keywords: form.keywords.trim() || null,
        }

        if (editingTopic) {
            await updateTopic.mutateAsync({ id: editingTopic.id, ...input })
        } else {
            await createTopic.mutateAsync(input)
        }
        handleClose()
    }

    const handleDelete = (topicId: number) => {
        deleteTopic.mutate(topicId)
    }

    const isSaving = createTopic.isLoading || updateTopic.isLoading

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                padding: theme.spacing(2),
                overflow: 'hidden',
            }}
        >
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing(2),
                }}
            >
                <Typography variant="h6">Topics</Typography>
                <Tooltip title="Add Topic">
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAdd}
                        style={{ textTransform: 'none' }}
                    >
                        Add Topic
                    </Button>
                </Tooltip>
            </Container>

            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    overflowY: 'auto',
                    minHeight: 0,
                    gap: theme.spacing(1.5),
                    padding: theme.spacing(0.5),
                }}
            >
                {isLoading ? (
                    <Container
                        style={{
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Container>
                ) : (
                    topics.map((topic) => (
                        <Card
                            key={topic.id}
                            variant="outlined"
                            style={{ flexShrink: 0 }}
                        >
                            <CardContent>
                                <Container
                                    style={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            fontWeight: theme.typography
                                                .fontWeightBold as number,
                                        }}
                                    >
                                        {formatTopicName(topic.name)}
                                    </Typography>
                                    <Container
                                        style={{ gap: 4, flexShrink: 0 }}
                                    >
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleOpenEdit(topic)
                                                }
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDelete(topic.id)
                                                }
                                                disabled={deleteTopic.isLoading}
                                            >
                                                <DeleteIcon
                                                    fontSize="small"
                                                    color="error"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Container>
                                </Container>
                                {topic.description && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            marginTop: theme.spacing(0.5),
                                        }}
                                    >
                                        {topic.description}
                                    </Typography>
                                )}
                                {topic.keywords && (
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{
                                            marginTop: theme.spacing(0.5),
                                            display: 'block',
                                        }}
                                    >
                                        Keywords: {topic.keywords}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    ))
                )}
            </Container>

            <Dialog
                open={dialogOpen}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {editingTopic ? 'Edit Topic' : 'Add Topic'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                name: e.target.value,
                            }))
                        }
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: theme.spacing(1) }}
                    />
                    <TextField
                        label="Description"
                        value={form.description}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                description: e.target.value,
                            }))
                        }
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={3}
                        style={{ marginTop: theme.spacing(2) }}
                    />
                    <TextField
                        label="Keywords"
                        value={form.keywords}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                keywords: e.target.value,
                            }))
                        }
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: theme.spacing(2) }}
                        helperText="Comma-separated keywords"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        style={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={!form.name.trim() || isSaving}
                        style={{ textTransform: 'none' }}
                    >
                        {isSaving ? 'Saving...' : editingTopic ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
